// What one row means, when neither its action nor its columns can say it. A block list is the clearest
// case: a created row is a prohibition ("the trip stops taking reservations"), a deleted one lifts it,
// so "U krijua" alone tells the reader the opposite of what happened. Entities whose rows mean
// different things depending on one column are keyed by that column's value first — the backend sends
// it as the row's variant — and the rest by the action alone.
type Meanings = Partial<Record<string, string>>;

// Creating says one thing, deleting it (either way) says the other, restoring says the first again.
const onOff = (created: string, deleted: string): Meanings => ({
  Created: created,
  SoftDeleted: deleted,
  HardDeleted: deleted,
  Restored: created
});

const PICTURE = onOff("Fotoja u shtua", "Fotoja u hoq");

const gtfsBlock = (subject: string): Meanings =>
  onOff(`${subject} nuk dërgohet më në GTFS`, `${subject} dërgohet sërish në GTFS`);

const VARIANT_ROW_MEANINGS: Record<string, Record<string, Meanings>> = {
  CustomTripWorkingDay: {
    ExceptionDate: {
      Created: "Orari nuk qarkullon në këtë datë",
      SoftDeleted: "Orari qarkullon sërish në këtë datë",
      HardDeleted: "Orari qarkullon sërish në këtë datë"
    },
    SelectedWorkingDate: {
      Created: "Orari qarkullon edhe në këtë datë",
      SoftDeleted: "Data shtesë u hoq",
      HardDeleted: "Data shtesë u hoq"
    }
  },
  GTFSBlockedEntity: {
    Operator: gtfsBlock("Operatori"),
    Line: gtfsBlock("Linja"),
    Trip: gtfsBlock("Orari")
  }
};

const ROW_MEANINGS: Record<string, Meanings> = {
  CustomTripReservabilityDate: onOff(
    "Orari nuk pranon rezervime në këtë datë",
    "Orari pranon sërish rezervime në këtë datë"
  ),
  BusTripMapping: onOff("Autobusi iu caktua orarit", "Autobusi u hoq nga orari"),
  PermissionRecordUserRoleMapping: onOff("Leja iu dha rolit", "Leja iu hoq rolit"),
  UserRoleMapping: onOff("Roli iu dha përdoruesit", "Roli iu hoq përdoruesit"),
  InvitedUserRoleMapping: onOff("Ftesa u bë për këtë rol", "Roli u hoq nga ftesa"),
  BlockedUser: onOff("Përdoruesi u bllokua", "Përdoruesi u zhbllokua"),
  TravelBundleLocationMapping: onOff("Qyteti u shtua në paketë", "Qyteti u hoq nga paketa"),
  TravelBundleIncludeMapping: onOff("Përfshirja u shtua në paketë", "Përfshirja u hoq nga paketa"),
  TravelBundlePictureMapping: PICTURE,
  BlogPictureMapping: PICTURE,
  EntityPictureMapping: PICTURE
};

// The sentence for one row, or null when the row's own fields say it better.
export const rowMeaning = (entityName: string, variant: string | null, action: string): string | null => {
  const byVariant = variant ? VARIANT_ROW_MEANINGS[entityName]?.[variant]?.[action] : undefined;
  return byVariant ?? ROW_MEANINGS[entityName]?.[action] ?? null;
};
